export type BubbleCategory = "free" | "premium";

export interface MessageBubble {
  id: string;
  name: string;
  category: BubbleCategory;
  price: number;
  tagline: string;
  /** Tailwind/CSS classes applied to OUTGOING (sent) bubble */
  meClass: string;
  /** Inline style for OUTGOING bubble (gradients, textures, glows) */
  meStyle?: React.CSSProperties;
  /** Optional inner overlay (e.g. texture image) for OUTGOING bubble */
  meOverlay?: React.CSSProperties;
  /** Text color for outgoing */
  meTextColor?: string;
  /** Optional border radius override */
  radius?: string;
}

// Common rounded shape — chats keep the trailing tail look
const R = "rounded-2xl rounded-br-md";
const RSharp = "rounded-lg rounded-br-sm";

export const MESSAGE_BUBBLES: MessageBubble[] = [
  {
    id: "classic-smooth",
    name: "Classic Smooth",
    category: "free",
    price: 0,
    tagline: "Default · clean gradient",
    meClass: R,
    meStyle: {
      background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent)))",
      boxShadow: "0 2px 12px -4px hsl(var(--primary) / 0.5)",
    },
    meTextColor: "hsl(var(--primary-foreground))",
  },
  {
    id: "rock-stone",
    name: "Rock Stone",
    category: "premium",
    price: 100,
    tagline: "Rugged stone texture",
    meClass: RSharp,
    meStyle: {
      background:
        "linear-gradient(135deg, #4a3f37 0%, #2e2722 50%, #5a4a3d 100%)",
      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.5), 0 4px 14px -4px rgba(0,0,0,0.6)",
      border: "1px solid rgba(0,0,0,0.5)",
    },
    meOverlay: {
      backgroundImage:
        "radial-gradient(circle at 20% 30%, rgba(0,0,0,0.35) 0 1px, transparent 2px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.06) 0 1px, transparent 2px), linear-gradient(115deg, transparent 40%, rgba(0,0,0,0.35) 41% 42%, transparent 43%)",
      backgroundSize: "12px 12px, 18px 18px, 100% 100%",
      mixBlendMode: "overlay",
    },
    meTextColor: "#f5ede3",
  },
  {
    id: "icy-frost",
    name: "Icy Frost",
    category: "premium",
    price: 100,
    tagline: "Frosted glass · ice crystals",
    meClass: "rounded-xl rounded-br-sm",
    meStyle: {
      background:
        "linear-gradient(135deg, rgba(186,230,253,0.85), rgba(125,211,252,0.7) 60%, rgba(224,242,254,0.85))",
      backdropFilter: "blur(8px)",
      border: "1px solid rgba(255,255,255,0.45)",
      boxShadow: "0 0 18px -4px rgba(125,211,252,0.6), inset 0 1px 0 rgba(255,255,255,0.6)",
    },
    meOverlay: {
      backgroundImage:
        "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%), radial-gradient(circle at 30% 40%, rgba(255,255,255,0.6) 0 1px, transparent 2px), radial-gradient(circle at 70% 70%, rgba(255,255,255,0.5) 0 1px, transparent 2px)",
      backgroundSize: "200% 100%, 14px 14px, 18px 18px",
    },
    meTextColor: "#0c2340",
  },
  {
    id: "neon-glow",
    name: "Neon Glow",
    category: "premium",
    price: 100,
    tagline: "Cyberpunk pulse",
    meClass: R,
    meStyle: {
      background: "#0a0a14",
      border: "1.5px solid #ff3df0",
      boxShadow:
        "0 0 12px #ff3df0, 0 0 24px rgba(34,211,238,0.55), inset 0 0 12px rgba(255,61,240,0.25)",
      animation: "neonPulse 2.4s ease-in-out infinite",
    },
    meTextColor: "#e0f7fa",
  },
  {
    id: "lava-flow",
    name: "Lava Flow",
    category: "premium",
    price: 100,
    tagline: "Molten rock · glowing cracks",
    meClass: RSharp,
    meStyle: {
      background:
        "linear-gradient(135deg, #1a0a05 0%, #3a1a08 100%)",
      boxShadow:
        "inset 0 0 12px rgba(255,80,0,0.4), 0 0 18px -4px rgba(255,90,0,0.6)",
      border: "1px solid #2a1208",
    },
    meOverlay: {
      backgroundImage:
        "linear-gradient(95deg, transparent 30%, rgba(255,120,30,0.85) 31% 32%, transparent 33%), linear-gradient(170deg, transparent 50%, rgba(255,60,0,0.7) 51% 52%, transparent 53%), linear-gradient(40deg, transparent 65%, rgba(255,180,40,0.7) 66% 67%, transparent 68%)",
      filter: "blur(0.4px) drop-shadow(0 0 4px #ff5a00)",
    },
    meTextColor: "#ffe0b8",
  },
  {
    id: "galaxy-bubble",
    name: "Galaxy Bubble",
    category: "premium",
    price: 100,
    tagline: "Nebula swirls · stars",
    meClass: R,
    meStyle: {
      background:
        "radial-gradient(ellipse at 30% 20%, #6b21a8 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, #1e3a8a 0%, transparent 55%), #050010",
      border: "1px solid rgba(168,85,247,0.4)",
      boxShadow: "0 0 18px -4px rgba(168,85,247,0.5)",
    },
    meOverlay: {
      backgroundImage:
        "radial-gradient(circle at 15% 25%, #fff 0 0.8px, transparent 1.4px), radial-gradient(circle at 65% 35%, #fff 0 0.6px, transparent 1.2px), radial-gradient(circle at 30% 75%, #fff 0 0.7px, transparent 1.3px), radial-gradient(circle at 85% 70%, #fff 0 0.6px, transparent 1.2px), radial-gradient(circle at 50% 50%, #fff 0 0.5px, transparent 1px)",
    },
    meTextColor: "#e9d5ff",
  },
  {
    id: "crystal-glass",
    name: "Crystal Glass",
    category: "premium",
    price: 100,
    tagline: "Transparent · rainbow shimmer",
    meClass: R,
    meStyle: {
      background: "rgba(255,255,255,0.12)",
      backdropFilter: "blur(10px)",
      border: "1px solid transparent",
      backgroundImage:
        "linear-gradient(rgba(255,255,255,0.12), rgba(255,255,255,0.12)), linear-gradient(120deg, #ff7eb3, #ff758c, #ffb86c, #6ee7b7, #60a5fa, #a78bfa, #ff7eb3)",
      backgroundOrigin: "border-box",
      backgroundClip: "padding-box, border-box",
      boxShadow: "0 4px 18px -6px rgba(255,255,255,0.4)",
    },
    meTextColor: "#ffffff",
  },
  {
    id: "gold-luxury",
    name: "Gold Luxury",
    category: "premium",
    price: 100,
    tagline: "Metallic gold shine",
    meClass: R,
    meStyle: {
      background:
        "linear-gradient(135deg, #b8860b 0%, #ffd700 25%, #fff8b0 45%, #ffd700 65%, #8b6914 100%)",
      border: "1px solid #8b6914",
      boxShadow:
        "0 4px 18px -4px rgba(255,215,0,0.6), inset 0 1px 0 rgba(255,255,255,0.5)",
    },
    meTextColor: "#3a2a05",
  },
  {
    id: "wooden",
    name: "Wooden",
    category: "premium",
    price: 100,
    tagline: "Warm wood grain",
    meClass: R,
    meStyle: {
      background:
        "linear-gradient(135deg, #6b4423 0%, #8b5a2b 50%, #5a3818 100%)",
      border: "1px solid #3a2412",
      boxShadow: "0 3px 10px -3px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
    },
    meOverlay: {
      backgroundImage:
        "repeating-linear-gradient(95deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 6px), repeating-linear-gradient(95deg, rgba(255,200,140,0.08) 0 2px, transparent 2px 14px)",
    },
    meTextColor: "#fce8c8",
  },
  {
    id: "carbon-fiber",
    name: "Carbon Fiber",
    category: "premium",
    price: 100,
    tagline: "Tech weave texture",
    meClass: RSharp,
    meStyle: {
      background: "#1a1a1a",
      border: "1px solid #333",
      boxShadow: "0 3px 12px -3px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)",
    },
    meOverlay: {
      backgroundImage:
        "repeating-linear-gradient(45deg, rgba(255,255,255,0.06) 0 2px, transparent 2px 4px), repeating-linear-gradient(-45deg, rgba(0,0,0,0.5) 0 2px, transparent 2px 4px)",
      backgroundSize: "6px 6px",
    },
    meTextColor: "#e5e5e5",
  },
];

export const DEFAULT_BUBBLE_ID = "classic-smooth";
export const FREE_BUBBLE_IDS = MESSAGE_BUBBLES.filter((b) => b.price === 0).map((b) => b.id);

export const getBubble = (id: string | null | undefined): MessageBubble =>
  MESSAGE_BUBBLES.find((b) => b.id === id) ?? MESSAGE_BUBBLES[0];